/**
 * Practice Listen narration helpers — device speech + studio clip matching.
 * Script wording must stay aligned with backend/ext/cueScript.js.
 */

/** Split step body into lead paragraphs + numbered “what to do” actions. */
export function splitBodyParts(body) {
  const lines = String(body || '').split('\n');
  const lead = [];
  const actions = [];
  for (const raw of lines) {
    const m = raw.match(/^\s*(\d+)\.\s+(.*)$/);
    if (m) actions.push(m[2].trim());
    else lead.push(raw);
  }
  while (lead.length && !lead[0].trim()) lead.shift();
  while (lead.length && !lead[lead.length - 1].trim()) lead.pop();
  return { lead: lead.join('\n').trim(), actions };
}

/** Plain text for speech — drop markdown emphasis / excess whitespace. */
export function stripForSpeech(text) {
  return String(text || '')
    .replace(/\*\*/g, '')
    .replace(/[_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Narration for Karma / Guided: inner cue + breath + numbered what-to-do. */
export function buildStepNarration(step) {
  if (!step) return '';
  const parts = [];
  const title = stripForSpeech(step.title);
  if (title) parts.push(`${title}.`);

  const cues = (step.say || []).map(stripForSpeech).filter(Boolean);
  if (cues.length) parts.push(cues.join(' '));

  const breaths = (step.breath || []).map(stripForSpeech).filter(Boolean);
  if (breaths.length) parts.push(`Breath. ${breaths.join(' ')}`);

  const { actions } = splitBodyParts(step.body);
  const doList = actions.map(stripForSpeech).filter(Boolean);
  if (doList.length) {
    parts.push(`What to do. ${doList.map((a, i) => `${i + 1}. ${a}`).join(' ')}`);
  }

  return parts.join(' … ').trim();
}

/** Same fingerprint the studio worker uses — skip re-queue when cues unchanged. */
export function cueFingerprint(text) {
  const s = String(text || '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${s.length.toString(16)}-${(h >>> 0).toString(16)}`;
}

/** Shared OpenAI studio guide — must match backend studioGuideWorker default. */
export const STUDIO_GUIDE_VOICE = 'shimmer';

/**
 * Queue patch when practitioner cues should (re)build studio clips.
 * Returns null when nothing to enqueue.
 */
export function studioAudioQueuePatch(cues, existingStudio) {
  const cuesText = String(cues || '');
  if (!cuesText.trim()) {
    if (!existingStudio || existingStudio.status === 'idle') return null;
    return {
      studioAudio: {
        status: 'idle',
        cueHash: cueFingerprint(''),
        steps: [],
        requestedAt: Date.now(),
      },
    };
  }
  const hash = cueFingerprint(cuesText);
  const st = existingStudio?.status;
  const voiceOk = String(existingStudio?.voice || '') === STUDIO_GUIDE_VOICE;
  if (existingStudio?.cueHash === hash) {
    if (st === 'queued' || st === 'running') return null;
    if (st === 'ready' && voiceOk) return null;
  }
  return {
    studioAudio: {
      status: 'queued',
      cueHash: hash,
      steps: [],
      requestedAt: Date.now(),
    },
  };
}

/** Ready studio clip URL for this step index, or null → use device voice. */
export function studioClipUrl(studioAudio, stepIndex, cues) {
  if (!studioAudio || studioAudio.status !== 'ready') return null;
  if (cueFingerprint(cues || '') !== studioAudio.cueHash) return null;
  const row = (studioAudio.steps || []).find((s) => s.index === stepIndex)
    || (studioAudio.steps || [])[stepIndex];
  const url = row?.url;
  return url ? String(url) : null;
}
